import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { FileText, Plus, X, Save, Trash2, Eye, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import ChatMedia, { MediaTypeIcon } from '@/components/ChatMedia';

const CATEGORIES = ['general', 'welcome', 'follow_up', 'reminder', 'closing'];

export default function WhatsAppTemplatesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState('');
  const [waCfg, setWaCfg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get('/whatsapp/templates');
    setItems(data.templates);
  };
  useEffect(() => {
    load();
    api.get('/whatsapp/config').then((r) => setWaCfg(r.data.config));
  }, []);

  const newTpl = () =>
    setEditing({
      name: '',
      category: 'general',
      body: 'Hola (nombre_cliente), te saluda (nombre_empleado) de (nombre_empresa). ¿Cómo puedo ayudarte?',
      enabled: true,
      media_type: null,
      media_filename: null,
      media_mimetype: null,
    });

  const save = async () => {
    try {
      if (!(editing.body || '').trim() && !editing.media_filename) {
        toast.error('Agrega texto o una imagen a la plantilla');
        return;
      }
      if (editing.id) await api.patch(`/whatsapp/templates/${editing.id}`, editing);
      else await api.post('/whatsapp/templates', editing);
      toast.success('Guardado');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };
  const del = async (t) => {
    if (!confirm(`Borrar "${t.name}"?`)) return;
    await api.delete(`/whatsapp/templates/${t.id}`);
    load();
  };
  const doPreview = async () => {
    if (!editing) return;
    const { data } = await api.post('/whatsapp/preview', { body: editing.body || '' });
    setPreview(data.rendered);
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo imágenes en plantillas (jpg, png, webp, gif)');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Máximo 25 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'templates');
      const { data } = await api.post('/whatsapp/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((prev) => ({
        ...prev,
        media_type: data.media_type || 'image',
        media_filename: data.media_filename,
        media_mimetype: data.media_mimetype,
      }));
      toast.success('Imagen adjunta a la plantilla');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setEditing((prev) => ({
      ...prev,
      media_type: null,
      media_filename: null,
      media_mimetype: null,
    }));
  };

  return (
    <div>
      <PageHeader
        title="Plantillas WhatsApp"
        description="Plantillas con variables dinámicas. Puedes adjuntar una foto que se enviará con el mensaje."
        actions={
          <button data-testid="wa-tpl-new" onClick={newTpl} className="btn-primary">
            <Plus className="h-4 w-4" /> Nueva plantilla
          </button>
        }
      />
      {!waCfg && (
        <div className="surface p-3 mb-4 text-sm border-l-2 border-[hsl(var(--warning))]">
          Sin WhatsApp configurado — los envíos usarán WhatsApp Web como fallback. Configura Cloud API o conexión por QR en Ajustes → WhatsApp.
        </div>
      )}
      {waCfg?.mode === 'qr' && (
        <div className="surface p-3 mb-4 text-sm border-l-2 border-primary">
          Modo QR activo: los envíos (texto + fotos) usan la sesión de WhatsApp Business vinculada.
        </div>
      )}
      {waCfg?.mode === 'cloud_api' && (
        <div className="surface p-3 mb-4 text-sm border-l-2 border-primary">
          Modo Cloud API (Meta) activo. Las fotos de plantilla se envían mejor en modo QR.
        </div>
      )}
      {items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Sin plantillas"
          description="Crea tu primera plantilla con variables como (nombre_cliente) y opcionalmente una foto."
          cta={
            <button onClick={newTpl} className="btn-primary">
              <Plus className="h-4 w-4" /> Crear
            </button>
          }
        />
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          {items.map((t) => (
            <div key={t.id} data-testid="wa-tpl-card" className="surface p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium flex items-center gap-1.5">
                    {t.name}
                    {t.media_filename && <MediaTypeIcon type={t.media_type || 'image'} className="h-4 w-4 opacity-70" />}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t.category} · {t.enabled ? 'activa' : 'inactiva'}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ ...t })} className="btn-ghost h-8 px-2 text-xs">
                    Editar
                  </button>
                  <button onClick={() => del(t)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {t.media_filename && (
                <div className="mt-3 max-w-[200px]">
                  <ChatMedia path={t.media_filename} mediaType={t.media_type || 'image'} mimetype={t.media_mimetype} />
                </div>
              )}
              <pre className="mt-3 text-sm whitespace-pre-wrap bg-secondary/40 rounded-[10px] p-3 max-h-32 overflow-y-auto">
                {t.body || '(sin texto)'}
              </pre>
              {(t.variables || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {t.variables.map((v) => (
                    <span key={v} className="chip">
                      {v}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">{editing.id ? 'Editar plantilla' : 'Nueva plantilla'}</h2>
              <button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label>
                  Nombre
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input mt-1" />
                </label>
                <label>
                  Categoría
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="input mt-1">
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Cuerpo
                  <textarea
                    data-testid="wa-tpl-body"
                    value={editing.body || ''}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    className="input min-h-[140px] mt-1 font-mono text-xs"
                    placeholder="Texto del mensaje (opcional si hay imagen)"
                  />
                </label>

                <div className="rounded-[12px] border border-border p-3 space-y-2">
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    <ImagePlus className="h-4 w-4" /> Foto del mensaje
                  </div>
                  <p className="text-xs text-muted-foreground">
                    La imagen se enviará junto con el texto al usar esta plantilla (Inbox o Lead).
                  </p>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
                  <div className="flex flex-wrap gap-2">
                    <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="btn-ghost text-xs">
                      {uploading ? 'Subiendo…' : editing.media_filename ? 'Cambiar imagen' : 'Adjuntar imagen'}
                    </button>
                    {editing.media_filename && (
                      <button type="button" onClick={clearImage} className="btn-ghost text-xs text-[hsl(var(--danger))]">
                        Quitar imagen
                      </button>
                    )}
                  </div>
                  {editing.media_filename && (
                    <div className="max-w-[220px] mt-1">
                      <ChatMedia path={editing.media_filename} mediaType={editing.media_type || 'image'} mimetype={editing.media_mimetype} />
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Plantilla activa
                </label>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium">Vista previa</h3>
                  <button data-testid="wa-tpl-preview" onClick={doPreview} className="btn-ghost h-8 px-2 text-xs">
                    <Eye className="h-3 w-3" /> Generar
                  </button>
                </div>
                <div className="rounded-[12px] border border-border bg-accent p-3 min-h-[180px] text-sm space-y-2">
                  {editing.media_filename && (
                    <ChatMedia path={editing.media_filename} mediaType={editing.media_type || 'image'} mimetype={editing.media_mimetype} />
                  )}
                  <div className="whitespace-pre-wrap">{preview || editing.body || '(sin texto)'}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  <p className="font-medium">Variables disponibles:</p>
                  <p>(nombre_cliente), (nombre_empleado), (nombre_empresa), (telefono_cliente), (ciudad), (servicio), (gestor_asignado), y cualquier campo personalizado.</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="btn-ghost">
                Cancelar
              </button>
              <button data-testid="wa-tpl-save" onClick={save} className="btn-primary">
                <Save className="h-4 w-4" /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
